import { useState } from "react";
import {
  IoMailOutline,
  IoSettingsOutline,
  IoDocumentOutline,
} from "react-icons/io5";

import AccountsFilterForm from "./components/AccountsFilterForm";
import AccountsTable from "./components/AccountsTable";
import SwornDeclarationForm from "./components/SwornDeclarationForm";
import TextboxSearch from "../../components/common/TextboxSearch";
import TableControls from "../../components/common/TableControls";
import TableSize from "../../components/common/TableSize";
import Modal from "../../components/common/Modal";
import Dropdown from "./../../components/common/Dropdown";

import reportService from "../../services/reportService";
import Toaster from "./../../components/common/Toaster";

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [showFilterForm, setShowFilterForm] = useState(false);
  const [showSwornForm, setShowSwornForm] = useState(false);

  const [tableOptions, setTableOptions] = useState({
    selected_statuses: "",
    start_date: "",
    end_date: "",
    search_query: "",
    current_page: 1,
    page_size: 10,
    sort_column: { path: "created_on", order: "desc" },
  });

  const columns = [
    { path: "account_number", label: "Account No." },
    { path: "name", label: "Name" },
    { path: "email", label: "Email" },
    { path: "account_status", label: "Status" },
    { path: "created_on", label: "Date Registered" },
  ];

  const { page_size: size } = tableOptions;

  const settings = [
    {
      id: 1,
      content: <TableSize size={size} onChange={(e) => handleSizeChange(e)} />,
    },
  ];

  const actions = [
    [
      {
        name: <span className="text-gray-400">Send Sworn Declaration</span>,
        icon: <IoMailOutline className="mr-5 text-gray-400" />,
        action: () => setShowSwornForm(true),
      },
    ],
  ];

  const handleOptionsChange = (options) => setTableOptions(options);

  const handleSizeChange = (event) => {
    const page_size = event.target.value;

    if (page_size !== tableOptions.page_size) {
      const options = { ...tableOptions };
      options.page_size = page_size;
      options.current_page = 1;
      setTableOptions(options);
    }
  };

  const handleSearch = (query) => {
    const options = { ...tableOptions };

    if (options.search_query !== query) {
      options.selected_statuses = "";
      options.start_date = "";
      options.end_date = "";
      options.search_query = query;
      options.current_page = 1;
      handleOptionsChange(options);
    }
  };

  const handleFilter = (statuses, start_date, end_date) => {
    const options = { ...tableOptions };
    options.selected_statuses = statuses;
    options.current_page = 1;
    options.start_date = start_date;
    options.end_date = end_date;
    handleOptionsChange(options);
    setShowFilterForm(false);
  };

  const handleExport = async () => {
    try {
      const { result } = await reportService.generateAccountsReportAsync();

      const mediaType =
        "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,";

      const link = document.createElement("a");
      link.href = mediaType + result.content;
      link.download = result.file_name;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    }
  };

  return (
    <div className="mt-2">
      <div className="max-w-7xl mx-auto px-6 lg:px-0">
        <div className="card p-8">
          <div className="block sm:flex justify-between items-center">
            <div>
              <p className="text-gray-500">Account Management</p>
              <span className="text-gray-400 text-sm">List of Accounts</span>
            </div>
          </div>
          <div className="block sm:flex justify-between items-center mt-12">
            <TextboxSearch placeholder="Search..." onSearch={handleSearch} />
            <div className="flex gap-2">
              <button
                title="Export to Excel"
                className="button cursor-pointer h-10 border group hover:border-blue-500"
                onClick={() => handleExport()}
              >
                <IoDocumentOutline className="text-gray-400 group-hover:text-blue-500" />
              </button>
              <TableControls
                onFilter={() => setShowFilterForm(true)}
                settings={settings}
              />
            </div>
          </div>
          <div className="mt-8">
            <AccountsTable
              data={accounts}
              columns={columns}
              tableOptions={tableOptions}
              setData={setAccounts}
              onChange={handleOptionsChange}
            />
            <Modal
              title="Filter"
              isOpen={showFilterForm}
              onClose={() => setShowFilterForm(false)}
              borderTop
              children={<AccountsFilterForm onFilter={handleFilter} />}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accounts;
